/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Telemetry & Executive Business Intelligence Dashboard View (Task 11.6.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  RefreshCw,
} from 'lucide-react';
import { SophiaIcon, type SophiaDefaultIconName } from '@sofia/assets';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useI18n } from '../i18n';
import { getAdminDomains } from '../lib/urls';
import { getAdminToken } from '../lib/api-client';
import type { AdminViewId } from '../components/layout/AdminSidebar';
import type {
  AdminAnalyticsResponse,
  AdminAnalyticsPeriod,
} from '@sofia/shared-types';

export const AdminDashboardView: React.FC<{ onNavigate: (view: AdminViewId) => void }> = ({
  onNavigate,
}) => {
  const { t, locale } = useI18n();
  const domains = getAdminDomains();

  const [period, setPeriod] = useState<AdminAnalyticsPeriod>('30d');
  const [loading, setLoading] = useState<boolean>(true);
  const [analyticsData, setAnalyticsData] = useState<AdminAnalyticsResponse | null>(null);

  // Formatter utilities
  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat(locale === 'vi' ? 'vi-VN' : 'en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(val);
  };

  const formatNumber = (val: number) => {
    return new Intl.NumberFormat(locale === 'vi' ? 'vi-VN' : 'en-US').format(val);
  };

  const fetchAnalytics = async (selectedPeriod: AdminAnalyticsPeriod) => {
    try {
      setLoading(true);
      const token = getAdminToken() || '';
      const res = await fetch(
        `${domains.apiOrigin}/api/v1/admin/analytics/dashboard?period=${selectedPeriod}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        }
      );

      if (res.ok) {
        const json = (await res.json()) as { success: boolean; data: AdminAnalyticsResponse };
        if (json.success && json.data) {
          setAnalyticsData(json.data);
        }
      }
    } catch {
      // Graceful fallback to cached state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics(period);
  }, [period]);

  const defaultKpi: AdminAnalyticsResponse['kpi'] = {
    mrr: 0,
    arr: 0,
    mau: 0,
    totalWorkspaces: 0,
    newWorkspaces: 0,
    trialToPaidRate: 0,
    activeSubscriptions: 0,
    churnRate: 0,
    totalRevenue: 0,
    growthRates: {
      mrr: 0,
      mau: 0,
      workspaces: 0,
      paidConversion: 0,
    },
  };

  const kpi = analyticsData?.kpi ?? defaultKpi;

  const isCleanState =
    !loading &&
    kpi.mrr === 0 &&
    kpi.totalWorkspaces === 0 &&
    kpi.activeSubscriptions === 0 &&
    kpi.totalRevenue === 0;

  const revenueTimeline = analyticsData?.revenueTimeline ?? [];
  const subscriptionTimeline = analyticsData?.subscriptionTimeline ?? [];
  const paymentGateways = analyticsData?.paymentGatewayDistribution ?? [];

  const edgeServices: {
    name: string;
    status: string;
    region: string;
    icon: SophiaDefaultIconName;
  }[] = [
    { name: 'Cloudflare D1 Database', status: 'Optimal', region: 'Global Edge (Auto)', icon: 'server' },
    { name: 'Cloudflare R2 Object Storage', status: 'Optimal', region: 'Multi-Region Sovereign', icon: 'structure-server' },
    { name: 'Cloudflare KV (KeyPool & Flags)', status: 'Optimal', region: 'Ultra-low Latency Tier', icon: 'finger-scanner' },
    { name: 'Cloudflare Vectorize (Index)', status: 'Optimal', region: '768-dim Embeddings', icon: 'nano-tech' },
    { name: 'Cloudflare Workers AI & Gateway', status: 'Optimal', region: 'Fallback Route Active', icon: 'cpu' },
  ];

  return (
    <div className="space-y-6 animate-fade-in pb-12">
      {/* Executive Header with Period Selector */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 rounded-2xl bg-gradient-to-r from-primary/10 via-surface to-accent/10 border border-surface-border">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-heading font-bold text-foreground">
              {t('admin.title')}
            </h2>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20">
              <SophiaIcon name="algorithm" size={12} className="text-primary shrink-0" />
              BI v2.0
            </span>
          </div>
          <p className="text-xs text-muted-fg max-w-2xl">
            {t('admin.subtitle')}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Timeframe Pills */}
          <div className="inline-flex items-center p-1 rounded-xl bg-surface-elevated border border-surface-border">
            {(['7d', '30d', '90d', '12m'] as AdminAnalyticsPeriod[]).map((p) => {
              const labelKey = `admin.dashboard.period${p}` as const;
              const isActive = period === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-fg shadow-sm'
                      : 'text-muted-fg hover:text-foreground'
                  }`}
                >
                  {t(labelKey)}
                </button>
              );
            })}
          </div>

          {/* Refresh Button */}
          <button
            type="button"
            onClick={() => fetchAnalytics(period)}
            disabled={loading}
            className="p-2 rounded-xl border border-surface-border bg-surface hover:bg-surface-elevated text-muted-fg hover:text-foreground transition-colors disabled:opacity-50"
            title={t('admin.dashboard.refreshData')}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-primary' : ''}`} />
          </button>

          <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
            <SophiaIcon name="server" size={13} className="text-emerald-500 shrink-0" />
            <span>{t('admin.dashboard.operational')}</span>
          </span>
        </div>
      </div>

      {/* Clean State Informational Banner */}
      {isCleanState && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-2xl border border-sky-500/20 bg-sky-500/10 text-sky-600 dark:text-sky-300">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-sky-500/20 text-sky-500 shrink-0">
              <SophiaIcon name="server" size={20} />
            </div>
            <div className="space-y-0.5">
              <h4 className="text-xs font-bold text-foreground">
                {t('admin.dashboard.cleanStateTitle')}
              </h4>
              <p className="text-[11px] text-muted-fg max-w-xl">
                {t('admin.dashboard.cleanStateDesc')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onNavigate('settings')}
            className="px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-sky-600 hover:bg-sky-500 text-white shrink-0 transition-colors shadow-xs"
          >
            {t('admin.dashboard.goToDemoManager')}
          </button>
        </div>
      )}

      {/* Core Executive KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* KPI 1: MRR & ARR */}
        <div className="p-5 rounded-xl border border-surface-border bg-surface shadow-sm space-y-3 relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-fg">{t('admin.dashboard.mrrTitle')}</span>
            <div className="p-2 rounded-lg border text-emerald-500 bg-emerald-500/10 border-emerald-500/20">
              <SophiaIcon name="digital-wallet" size={16} />
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-heading font-bold text-foreground">
                {formatCurrency(kpi.mrr)}
              </span>
              <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-500">
                <TrendingUp className="w-3 h-3" />
                +{kpi.growthRates.mrr}%
              </span>
            </div>
            <div className="flex items-center justify-between text-[11px] text-muted-fg pt-1">
              <span>{t('admin.dashboard.arrBadge')}:</span>
              <span className="font-semibold text-foreground font-mono">
                {formatCurrency(kpi.arr)}
              </span>
            </div>
          </div>
        </div>

        {/* KPI 2: MAU & Workspaces */}
        <div className="p-5 rounded-xl border border-surface-border bg-surface shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-fg">{t('admin.dashboard.mauTitle')}</span>
            <div className="p-2 rounded-lg border text-primary bg-primary/10 border-primary/20">
              <SophiaIcon name="city-space" size={16} />
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-heading font-bold text-foreground">
                {formatNumber(kpi.mau)}
              </span>
              <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-500">
                <TrendingUp className="w-3 h-3" />
                +{kpi.growthRates.mau}%
              </span>
            </div>
            <div className="flex items-center justify-between text-[11px] text-muted-fg pt-1">
              <span>{t('admin.dashboard.workspacesBadge')}:</span>
              <span className="font-semibold text-foreground font-mono">
                {formatNumber(kpi.totalWorkspaces)} (+{kpi.newWorkspaces})
              </span>
            </div>
          </div>
        </div>

        {/* KPI 3: Trial-to-Paid Conversion */}
        <div className="p-5 rounded-xl border border-surface-border bg-surface shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-fg">
              {t('admin.dashboard.conversionTitle')}
            </span>
            <div className="p-2 rounded-lg border text-accent bg-accent/10 border-accent/20">
              <SophiaIcon name="strategy" size={16} />
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-heading font-bold text-foreground">
                {kpi.trialToPaidRate}%
              </span>
              <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-500">
                <TrendingUp className="w-3 h-3" />
                +{kpi.growthRates.paidConversion}%
              </span>
            </div>
            <div className="flex items-center justify-between text-[11px] text-muted-fg pt-1">
              <span>{t('admin.dashboard.activeSubsBadge')}:</span>
              <span className="font-semibold text-foreground font-mono">
                {formatNumber(kpi.activeSubscriptions)}
              </span>
            </div>
          </div>
        </div>

        {/* KPI 4: Churn Rate */}
        <div className="p-5 rounded-xl border border-surface-border bg-surface shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-fg">{t('admin.dashboard.churnTitle')}</span>
            <div className="p-2 rounded-lg border text-sky-500 bg-sky-500/10 border-sky-500/20">
              <SophiaIcon name="battery" size={16} />
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-heading font-bold text-foreground">
                {kpi.churnRate}%
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                {t('admin.dashboard.healthyBenchmark')}
              </span>
            </div>
            <div className="flex items-center justify-between text-[11px] text-muted-fg pt-1">
              <span>{t('admin.dashboard.totalRevenue')}:</span>
              <span className="font-semibold text-foreground font-mono">
                {formatCurrency(kpi.totalRevenue)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Multi-Dimensional Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart 1: Revenue Breakdown AreaChart */}
        <div className="p-5 rounded-xl border border-surface-border bg-surface shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-surface-border pb-3">
            <div>
              <h3 className="text-sm font-heading font-semibold text-foreground">
                {t('admin.dashboard.revenueChartTitle')}
              </h3>
              <p className="text-[11px] text-muted-fg">
                {t('admin.dashboard.revenueChartDesc')}
              </p>
            </div>
            <span className="text-xs font-semibold text-foreground font-mono">
              {formatCurrency(kpi.totalRevenue)}
            </span>
          </div>

          <div className="h-[280px] w-full pt-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueTimeline} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorStripe" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="colorPaypal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0284c7" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#0284c7" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="colorBank" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-surface-border, #334155)" opacity={0.3} />
                <XAxis dataKey="date" stroke="var(--color-muted-fg, #94a3b8)" fontSize={11} tickLine={false} />
                <YAxis
                  stroke="var(--color-muted-fg, #94a3b8)"
                  fontSize={11}
                  tickLine={false}
                  tickFormatter={(val) => `$${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-surface, #1e293b)',
                    borderColor: 'var(--color-surface-border, #334155)',
                    borderRadius: '0.75rem',
                    fontSize: '11px',
                    color: 'var(--color-foreground, #f8fafc)',
                  }}
                  formatter={(val: any) => [`$${formatNumber(Number(val) || 0)}`, '']}
                />
                <Legend
                  verticalAlign="top"
                  align="right"
                  height={30}
                  iconType="circle"
                  wrapperStyle={{ fontSize: '11px' }}
                />
                <Area
                  type="monotone"
                  dataKey="stripe"
                  name={t('admin.dashboard.stripe')}
                  stroke="#10b981"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorStripe)"
                />
                <Area
                  type="monotone"
                  dataKey="paypal"
                  name={t('admin.dashboard.paypal')}
                  stroke="#0284c7"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorPaypal)"
                />
                <Area
                  type="monotone"
                  dataKey="bankTransfer"
                  name={t('admin.dashboard.bankTransfer')}
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorBank)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 2: Subscribers Dynamics BarChart */}
        <div className="p-5 rounded-xl border border-surface-border bg-surface shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-surface-border pb-3">
            <div>
              <h3 className="text-sm font-heading font-semibold text-foreground">
                {t('admin.dashboard.subscribersChartTitle')}
              </h3>
              <p className="text-[11px] text-muted-fg">
                {t('admin.dashboard.subscribersChartDesc')}
              </p>
            </div>
            <span className="text-xs font-semibold text-emerald-500 font-mono">
              +{kpi.growthRates.mrr}% growth
            </span>
          </div>

          <div className="h-[280px] w-full pt-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={subscriptionTimeline} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-surface-border, #334155)" opacity={0.3} />
                <XAxis dataKey="date" stroke="var(--color-muted-fg, #94a3b8)" fontSize={11} tickLine={false} />
                <YAxis stroke="var(--color-muted-fg, #94a3b8)" fontSize={11} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-surface, #1e293b)',
                    borderColor: 'var(--color-surface-border, #334155)',
                    borderRadius: '0.75rem',
                    fontSize: '11px',
                    color: 'var(--color-foreground, #f8fafc)',
                  }}
                />
                <Legend
                  verticalAlign="top"
                  align="right"
                  height={30}
                  iconType="circle"
                  wrapperStyle={{ fontSize: '11px' }}
                />
                <Bar
                  dataKey="newSubscribers"
                  name={t('admin.dashboard.newSubs')}
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="churnedSubscribers"
                  name={t('admin.dashboard.churnedSubs')}
                  fill="#f43f5e"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="netGrowth"
                  name={t('admin.dashboard.netGrowth')}
                  fill="#0ea5e9"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Payment Gateway Distribution Breakdown */}
      <div className="p-5 rounded-xl border border-surface-border bg-surface shadow-sm space-y-4">
        <div className="flex items-center justify-between border-b border-surface-border pb-3">
          <div>
            <h3 className="text-sm font-heading font-semibold text-foreground">
              {t('admin.dashboard.gatewayShareTitle')}
            </h3>
            <p className="text-[11px] text-muted-fg">
              {t('admin.dashboard.gatewayShareDesc')}
            </p>
          </div>
        </div>

        {paymentGateways.length === 0 ? (
          <div className="py-8 px-4 text-center rounded-xl border border-dashed border-surface-border bg-surface-elevated/20 flex flex-col items-center justify-center space-y-2">
            <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
              <SophiaIcon name="digital-wallet" size={20} />
            </div>
            <p className="text-xs font-semibold text-foreground">
              {t('admin.dashboard.noGatewaysData')}
            </p>
            <p className="text-[11px] text-muted-fg max-w-sm">
              {t('admin.dashboard.noGatewaysDesc')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {paymentGateways.map((gw, i) => (
              <div
                key={i}
                className="p-4 rounded-xl border border-surface-border bg-surface-elevated/40 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-foreground">{gw.label}</span>
                  <span className="text-xs font-mono font-bold text-primary">{gw.percentage}%</span>
                </div>

                {/* Progress bar */}
                <div className="w-full bg-surface-border rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-2 rounded-full ${
                      gw.gateway === 'stripe'
                        ? 'bg-emerald-500'
                        : gw.gateway === 'paypal'
                        ? 'bg-sky-500'
                        : 'bg-violet-500'
                    }`}
                    style={{ width: `${gw.percentage}%` }}
                  />
                </div>

                <div className="flex items-center justify-between text-[11px] text-muted-fg font-mono">
                  <span>{formatCurrency(gw.amount)}</span>
                  <span>{gw.count} txns</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Services Status & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Edge Infrastructure Status */}
        <div className="lg:col-span-2 p-5 rounded-xl border border-surface-border bg-surface shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-surface-border pb-3">
            <div className="flex items-center gap-2">
              <SophiaIcon name="hoverboard" size={16} className="text-primary shrink-0" />
              <h3 className="text-sm font-heading font-semibold text-foreground">
                {t('admin.dashboard.serverlessUptime')}
              </h3>
            </div>
            <span className="text-xs text-muted-fg font-mono">CF Zone: Active</span>
          </div>

          {/* Cloudflare Edge Health Telemetry Metrics Quad Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1 pb-1">
            <div className="p-3 rounded-xl bg-surface-elevated/50 border border-surface-border space-y-1">
              <div className="flex items-center gap-1.5 text-primary">
                <SophiaIcon name="hoverboard" size={14} className="shrink-0" />
                <span className="text-[11px] font-semibold text-foreground truncate">{t('admin.dashboard.healthWorkers')}</span>
              </div>
              <div className="text-base font-heading font-bold text-foreground">48.2k rps</div>
              <p className="text-[10px] text-muted-fg truncate">{t('admin.dashboard.healthWorkersDesc')}</p>
            </div>

            <div className="p-3 rounded-xl bg-surface-elevated/50 border border-surface-border space-y-1">
              <div className="flex items-center gap-1.5 text-emerald-500">
                <SophiaIcon name="server" size={14} className="shrink-0" />
                <span className="text-[11px] font-semibold text-foreground truncate">{t('admin.dashboard.healthD1')}</span>
              </div>
              <div className="text-base font-heading font-bold text-foreground">12.4k r/s</div>
              <p className="text-[10px] text-muted-fg truncate">{t('admin.dashboard.healthD1Desc')}</p>
            </div>

            <div className="p-3 rounded-xl bg-surface-elevated/50 border border-surface-border space-y-1">
              <div className="flex items-center gap-1.5 text-accent">
                <SophiaIcon name="structure-server" size={14} className="shrink-0" />
                <span className="text-[11px] font-semibold text-foreground truncate">{t('admin.dashboard.healthR2')}</span>
              </div>
              <div className="text-base font-heading font-bold text-foreground">1.84 TB</div>
              <p className="text-[10px] text-muted-fg truncate">{t('admin.dashboard.healthR2Desc')}</p>
            </div>

            <div className="p-3 rounded-xl bg-surface-elevated/50 border border-surface-border space-y-1">
              <div className="flex items-center gap-1.5 text-cyan-500">
                <SophiaIcon name="windmill" size={14} className="shrink-0" />
                <span className="text-[11px] font-semibold text-foreground truncate">{t('admin.dashboard.healthCache')}</span>
              </div>
              <div className="text-base font-heading font-bold text-foreground">98.6%</div>
              <p className="text-[10px] text-muted-fg truncate">{t('admin.dashboard.healthCacheDesc')}</p>
            </div>
          </div>

          <div className="divide-y divide-surface-border">
            {edgeServices.map((svc, i) => (
              <div key={i} className="py-2.5 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                  <div className="p-1 rounded-md bg-surface-elevated border border-surface-border/60 shrink-0">
                    <SophiaIcon name={svc.icon} size={13} className="text-muted-fg" />
                  </div>
                  <span className="font-medium text-foreground">{svc.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-muted-fg text-[11px] font-mono">{svc.region}</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                    {svc.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions Panel */}
        <div className="p-5 rounded-xl border border-surface-border bg-surface shadow-sm space-y-4">
          <div className="flex items-center gap-2 border-b border-surface-border pb-3">
            <SophiaIcon name="algorithm" size={16} className="text-accent shrink-0" />
            <h3 className="text-sm font-heading font-semibold text-foreground">
              {t('admin.dashboard.quickActions')}
            </h3>
          </div>

          <div className="space-y-2">
            <button
              type="button"
              onClick={() => onNavigate('keypool')}
              className="w-full text-left p-3 rounded-lg border border-surface-border bg-surface-elevated/40 hover:bg-surface-elevated text-xs transition-colors space-y-0.5 group"
            >
              <div className="font-semibold text-foreground flex items-center justify-between">
                <span>KeyPool Monitor</span>
                <SophiaIcon name="finger-scanner" size={14} className="text-muted-fg group-hover:text-primary transition-colors shrink-0" />
              </div>
              <p className="text-[11px] text-muted-fg">{t('admin.dashboard.keypoolSummary')}</p>
            </button>

            <button
              type="button"
              onClick={() => onNavigate('orders')}
              className="w-full text-left p-3 rounded-lg border border-surface-border bg-surface-elevated/40 hover:bg-surface-elevated text-xs transition-colors space-y-0.5 group"
            >
              <div className="font-semibold text-foreground flex items-center justify-between">
                <span>Review Wire Orders</span>
                <SophiaIcon name="digital-wallet" size={14} className="text-muted-fg group-hover:text-primary transition-colors shrink-0" />
              </div>
              <p className="text-[11px] text-muted-fg">{t('admin.dashboard.ordersSummary')}</p>
            </button>

            <button
              type="button"
              onClick={() => onNavigate('auditLogs')}
              className="w-full text-left p-3 rounded-lg border border-surface-border bg-surface-elevated/40 hover:bg-surface-elevated text-xs transition-colors space-y-0.5 group"
            >
              <div className="font-semibold text-foreground flex items-center justify-between">
                <span>View Audit Journal</span>
                <SophiaIcon name="eye-recognition" size={14} className="text-muted-fg group-hover:text-primary transition-colors shrink-0" />
              </div>
              <p className="text-[11px] text-muted-fg">{t('admin.dashboard.auditSummary')}</p>
            </button>

            <button
              type="button"
              onClick={() => onNavigate('seedAssets')}
              className="w-full text-left p-3 rounded-lg border border-surface-border bg-surface-elevated/40 hover:bg-surface-elevated text-xs transition-colors space-y-0.5 group"
            >
              <div className="font-semibold text-foreground flex items-center justify-between">
                <span>Seed Assets Syncer</span>
                <SophiaIcon name="big-data" size={14} className="text-muted-fg group-hover:text-primary transition-colors shrink-0" />
              </div>
              <p className="text-[11px] text-muted-fg">47 assets R2 sync with $0 egress</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
